import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import CodeIcon from "@mui/icons-material/Code";
import { Select, MenuItem, } from '@mui/material';
import { AppBar, Tabs, Tab } from "@mui/material";
import Typography from '@material-ui/core/Typography';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import TextField from '@mui/material/TextField';
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Device } from '@twilio/voice-sdk';
import { parsePhoneNumber } from 'react-phone-number-input';
import moment from 'moment-timezone';
import './dial-pad.css';
import CallIcon from './CallIcon';
import EndCallIcon from './EndCallIcon';
import BackspaceIcon from './BackspaceIcon';
import MakeCall from '../Activities/MakeCall';
import ConnectionStatus from '../Integration/ConnectionStatus';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other} >
      {value === index && (<Box p={3}> <Typography>{children}</Typography> </Box>)}
    </div>
  );
}

TabPanel.propTypes = { children: PropTypes.node, index: PropTypes.any['isRequired'], value: PropTypes.any['isRequired'] };

// Get local time for phone number dynamically
const getLocalTimeForPhoneNumber = (phoneNumber) => {
  try {
    if (!phoneNumber) return null;
    const parsed = parsePhoneNumber(phoneNumber);
    if (!parsed) return null;

    const countryCode = parsed.country;
    const timezonesForCountry = moment.tz.zonesForCountry(countryCode);
    if (!timezonesForCountry || timezonesForCountry.length === 0) return null;

    // Get timezone with largest population (most representative)
    let mainTimezone = timezonesForCountry[0];
    let maxPopulation = 0;

    timezonesForCountry.forEach(tz => {
      const zone = moment.tz.zone(tz);
      if (zone && zone.population > maxPopulation) { maxPopulation = zone.population; mainTimezone = tz; }
    });

    const localTime = moment.tz(mainTimezone);
    const cityName = mainTimezone.split('/').pop().replace(/_/g, ' ');
    const hour = localTime.hour();
    const isBusinessHours = hour >= 9 && hour < 18; // 9 AM to 6 PM

    return { time: localTime.format('h:mm A'), location: cityName, fullText: `${localTime.format('h:mm A')} local Time (${cityName}, ${countryCode})`, isBusinessHours };
  } catch (error) {
    console.error('Error parsing phone number for timezone:', error);
    return null;
  }
};

const DialPad = ({ setDialPadShow, initialPhoneNumber, setInitialPhoneNumber }) => {
  // Redux
  const dispatch = useDispatch();
  const history = useHistory();
  const { softphoneContacts } = useSelector(state => state.Phone);
  const contacts = softphoneContacts?.records || [];
  const contactsLoading = false;
  const { currentWorkspace } = useSelector(state => state.Settings);
  const { user: currentUser } = useSelector(state => state.Auth);
  const { twilioVoiceToken, twilioPhoneNumber, softphoneCallLogs, loading, twilioError, pendingCall } = useSelector(state => state.Phone);
  const allCallLogs = softphoneCallLogs || { callLogs: [] };
  // Get connection status from Integration store (unified approach)
  const integrationStatus = useSelector(state => state.Integration.integrationStatus);
  // Always use lowercase key for consistency (saga normalizes all keys to lowercase)
  const twilioStatus = integrationStatus?.twilio;
  const [call, setCall] = useState(false);
  const [code, setCode] = useState(initialPhoneNumber || '');
  const [selected, setSelected] = useState("1");
  const [valueTabUsing, setValueTabUsing] = useState(2);
  const [valueMeTab, setValueMeTab] = useState(1);
  const [callingDial, setCallingDial] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [twilioDevice, setTwilioDevice] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // idle, connecting, ringing, active, ended
  const [deviceReady, setDeviceReady] = useState(false);
  const [callingContactInfo, setCallingContactInfo] = useState(null); // Store contact info for active call
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [pendingCallNumber, setPendingCallNumber] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const callTimerRef = useRef(null);
  const pendingCallContextRef = useRef(null);
  const activeCallContextRef = useRef(null); // Store context during call for activity refresh
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollObserverRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const savedScrollPosition = useRef(0);

useEffect(() => {
    if (currentWorkspace?._id) {
      dispatch({ type: 'GET_SOFTPHONE_CONTACTS', payload: { page: 1, limit: 50 } });
      dispatch({ type: 'GET_SOFTPHONE_CALL_LOGS', payload: { filterObj: { callType: { filteredValue: { in: ['cold_calling'] }}}, page: 1, limit: 50 }});
    }
    if (currentWorkspace?._id || twilioStatus?.data?.isConnected) dispatch({ type: 'GET_TWILIO_VOICE_TOKEN' });
  }, [dispatch, currentWorkspace?._id, twilioStatus?.data?.isConnected]);

  // Reset pagination when switching tabs
  useEffect(() => { setCurrentPage(1); setHasMore(true) }, [valueTabUsing]);

  // Handle pending call from Redux
  useEffect(() => {
    if (pendingCall && deviceReady && twilioDevice && !pendingCall.justOpenDialpad) {
      setValueTabUsing(2);
      pendingCallContextRef.current = { ...pendingCall };
      setTimeout(() => {
        const contextFromRef = pendingCallContextRef.current;
        makeCall(contextFromRef?.phone, false, contextFromRef);
        dispatch({ type: 'CLEAR_PENDING_CALL' });
      }, 100);
    }
  }, [pendingCall, deviceReady, twilioDevice, dispatch]);

  useEffect(() => {
    if (initialPhoneNumber) {
      setCode(initialPhoneNumber);
      setValueTabUsing(2); 
      if (setInitialPhoneNumber) setInitialPhoneNumber(null);
    }
  }, [initialPhoneNumber]);

  useEffect(() => {
    if (!twilioVoiceToken) return;
    const initializeTwilioDevice = async () => {
      try {
        const device = new Device(twilioVoiceToken);
        device.on('registered', () => { setDeviceReady(true) });
        device.on('error', (error) => { setDeviceReady(false) });

        // Register the device
        await device.register();
        setTwilioDevice(device);
      } catch (error) {
        console.error('Failed to initialize Twilio Device:', error);
        setDeviceReady(false);
      }
    };
    initializeTwilioDevice();

    return () => { if (twilioDevice) twilioDevice.destroy() };
  }, [twilioVoiceToken]);

  // Load more data for infinite scroll
  const loadMoreData = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    // Save scroll position before loading new data
    if (scrollContainerRef.current) savedScrollPosition.current = scrollContainerRef.current.scrollTop;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    if (valueTabUsing === 0) dispatch({ type: 'GET_SOFTPHONE_CALL_LOGS', payload: { filterObj: { callType: { filteredValue: { in: ['cold_calling'] }}}, page: nextPage, limit: 50}});
    else if (valueTabUsing === 1) dispatch({ type: 'GET_SOFTPHONE_CONTACTS', payload: { page: nextPage, limit: 50 }});
    setCurrentPage(nextPage);
  }, [isLoadingMore, hasMore, currentPage, valueTabUsing, dispatch]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const options = { root: scrollContainerRef.current, rootMargin: '100px', threshold: 0.1};

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) loadMoreData();
    }, options);

    if (scrollObserverRef.current) observer.observe(scrollObserverRef.current);

    return () => {
      if (scrollObserverRef.current) observer.unobserve(scrollObserverRef.current);
    };
  }, [hasMore, isLoadingMore, loadMoreData]);

  // Update hasMore and reset loading when data changes
  useEffect(() => {
    if (valueTabUsing === 0) {
      const callLogsCount = allCallLogs?.callLogs?.length || 0;
      if (callLogsCount > 0 && callLogsCount < currentPage * 50) setHasMore(false);
      setIsLoadingMore(false);
    } else if (valueTabUsing === 1) {
      const contactsCount = contacts?.length || 0;
      if (contactsCount > 0 && contactsCount < currentPage * 50) setHasMore(false);
      setIsLoadingMore(false);
    }
  }, [allCallLogs, contacts, valueTabUsing, currentPage]);

  // Restore scroll position after new data loads
  useEffect(() => {
    if (!isLoadingMore && savedScrollPosition.current > 0 && scrollContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {  scrollContainerRef.current.scrollTop = savedScrollPosition.current; savedScrollPosition.current = 0 }
      });
    }
  }, [isLoadingMore, allCallLogs, contacts]);

  // Filter contacts based on search
  const filteredContacts = contacts.filter(contact => {
    if (!contactSearch) return true;
    const searchLower = contactSearch.toLowerCase();
    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
    const phone = contact.phone || '';
    return fullName.includes(searchLower) || phone.includes(searchLower);
  });

  // Filter call logs using filteredContacts
  const filteredCallLogs = (allCallLogs?.callLogs || []).filter(log => {
    if (!contactSearch) return true;
    const searchLower = contactSearch.toLowerCase();
    const phoneNumber = log.toNumber || log.fromNumber || '';
    if (phoneNumber.includes(searchLower)) return true;
    return filteredContacts.some(contact => contact.phone === phoneNumber);
  });

  // Handle contact phone click - make direct call
  const handleContactPhoneClick = (phone, contact) => {
    if (phone) { setCallingContactInfo({  name: `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Unknown', phone: phone, initials: getContactInitials(contact), contactId: contact?._id || null }); setValueTabUsing(2); makeCall(phone); }
  };

  // Get contact initials for avatar
  const getContactInitials = (contact) => {
    const firstName = contact.firstName || '';
    const lastName = contact.lastName || '';
    if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    else if (firstName) return firstName.charAt(0).toUpperCase();
    return 'U';
  };

  // Format call duration as MM:SS
  const formatCallDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Call timer effect - starts when call is active, stops when ended
  useEffect(() => {
    if (callStatus === 'active' || callStatus === 'Muted' || callStatus === 'On Hold') {
      // Start timer if not already running
      if (!callTimerRef.current) callTimerRef.current = setInterval(() => { setCallDuration(prev => prev + 1)}, 1000);
    } else {
      // Stop timer and reset when call ends
      if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null }
      if (callStatus === 'idle' || callStatus === 'ended') setCallDuration(0);
    }
    return () => {
      if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null }
    };
  }, [callStatus]);

  // Get call status display text
  const getCallStatusText = () => {
    switch (callStatus) {
      case 'connecting':
        return 'Connecting...';
      case 'ringing':
        return 'Ringing...';
      case 'active':
        return formatCallDuration(callDuration);
      case 'ended':
        return 'Call Ended';
      case 'Muted':
        return formatCallDuration(callDuration);
      case 'On Hold':
        return formatCallDuration(callDuration);
      default:
        return 'Calling...';
    }
  };

  // Helper functions for call logs
  const getCallLogContactName = (log) => {
    const phoneNumber = log.toNumber || log.fromNumber || 'Unknown';
    const contact = contacts?.find(c => c.phone === phoneNumber);
    return contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : 'Unknown';
  };

  const getCallLogInitials = (log) => {
    const phoneNumber = log.toNumber || log.fromNumber || '';
    const contact = contacts?.find(c => c.phone === phoneNumber);
    if (contact) return getContactInitials(contact);
    return 'U';
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minute${diff >= 120 ? 's' : ''} ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour${diff >= 7200 ? 's' : ''} ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} day${diff >= 172800 ? 's' : ''} ago`;
    return new Date(date).toLocaleDateString();
  };

  // Navigate to contact detail page
  const navigateToContact = (contactId) => {
    if (!contactId || !currentWorkspace?._id) return;
    setDialPadShow(false);
    const targetUrl = `/${currentWorkspace._id}/contacts/${contactId}`;
    if (window.location.pathname.includes('/contacts/')) window.location.href = targetUrl;
    else history.push(targetUrl);
  };

  // Navigate to Phones page to view call log details
  const handleCallLogClick = (log) => {
    if (!log?._id || !currentWorkspace?._id) return;
    setDialPadShow(false);
    history.push(`/${currentWorkspace._id}/phones?tab=callLogs&callLogId=${log._id}`);
  };

  // Get contact from call log (from backend), optionally navigate to activity page
  const getContactFromLog = (log, navigate = false) => {
    if (!log?.activityId || !log?.contact?._id) return null;
    if (navigate && currentWorkspace?._id) { setDialPadShow(false); history.push(`/${currentWorkspace._id}/contacts/${log.contact._id}?tab=all&activityId=${log.activityId}`); }
    return log.contact;
  };

  // Match contacts based on typed digits
  const matchedContacts = useMemo(() => {
    if (!code) return [];
    const normalizedCode = code.replace(/[^\d]/g, '');
    if (!normalizedCode) return [];
    return contacts.filter(contact => {
      const contactPhone = (contact.phone || '').replace(/[^\d]/g, '');
      return contactPhone.includes(normalizedCode);
    });
  }, [code, contacts]);

  // ✅ Update the parent state when code changes
  useEffect(() => {
    if (setCall && typeof setCall === 'function') setCall({ ...call, phone: code });
  }, [code]);

  // ✅ Reset code safely when call.status changes
  useEffect(() => {
    if (call?.status) setCode('');
  }, [call?.status]);

  // ✅ Safe digit click handler
  const handleDigitClick = useCallback(
    (value) => () => {
      setCode((prevCode) => prevCode.concat(value));
      // Send DTMF tones during active call for IVR navigation
      if (currentCall && currentCall.status() === 'open') { currentCall.sendDigits(value);}
    }, [currentCall]
  );

  // ✅ Backspace handler
  const handleBackspaceClick = useCallback(() => {setCode((prevCode) => prevCode.slice(0, -1)) }, []);

  // Make a call using Twilio Device
  const makeCall = useCallback(async (phoneNumber = null, skipWarning = false, callContext = null) => {
    console.log('📞 makeCall invoked with args - phoneNumber:', phoneNumber, 'skipWarning:', skipWarning, 'callContext:', JSON.stringify(callContext));
    if (!twilioDevice || !deviceReady) return

    const numberToCall = (typeof phoneNumber === 'string' ? phoneNumber : null) || code;
    if (!numberToCall || typeof numberToCall !== 'string' || numberToCall.trim() === '') return;

    // Check business hours
    if (!skipWarning) {
      const timeInfo = getLocalTimeForPhoneNumber(numberToCall);
      if (timeInfo && !timeInfo.isBusinessHours) {
        // Store context in ref so it survives when "Call Anyway" is clicked
        if (callContext) pendingCallContextRef.current = callContext;
        setPendingCallNumber(numberToCall);
        setShowTimeWarning(true);
        return;
      }
    }

    try {
      // Clear previous call state first
      setCallingContactInfo(null);
      setCallStatus('idle');

      // Find contact info for this number
      const normalizedDialedNumber = numberToCall.replace(/[^\d]/g, '');
      const contact = contacts.find(c => {
        const contactPhone = (c.phone || '').replace(/[^\d]/g, '');
        return contactPhone === normalizedDialedNumber;
      });

      // Store contact info or just the number
      if (contact) setCallingContactInfo({ name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '', phone: numberToCall, initials: getContactInitials(contact), contactId: contact._id });
      else setCallingContactInfo({ name: 'Unknown', phone: numberToCall, initials: 'U' });

      // Build objects array for activity creation (format: objectType:objectId)
      const objectPairs = [];
      let contactId = callingContactInfo?.contactId || (contact?._id || '');

      // Use callContext if passed, otherwise fall back to ref, then pendingCall state
      const contextToUse = callContext || pendingCallContextRef.current || pendingCall;

      // If called from a page (via pendingCall.contact from Activities)
      if (contextToUse?.contact) {
        const { _id, objectType, parentId, parentType } = contextToUse.contact;
        if (objectType === 'contacts') contactId = _id;
        else if (objectType) objectPairs.push(`${objectType}:${_id}`);

        // If contact was selected from a deal/company page, also link to parent
        if (parentId && parentType) objectPairs.push(`${parentType}:${parentId}`);
        // If calling from deal/company, use the matched contact's ID
        if (!contactId && contact) contactId = contact._id;
      }

      // Add contact to objects if exists
      if (contactId) objectPairs.unshift(`contacts:${contactId}`);

      // Store context for activity refresh after call ends
      activeCallContextRef.current = contextToUse?.contact ? {
        objectType: contextToUse.contact.parentType || contextToUse.contact.objectType,
        objectId: contextToUse.contact.parentId || contextToUse.contact._id
      } : null;

      setCallStatus('connecting');
      setCallingDial(true);
      const call = await twilioDevice.connect({ params: { To: numberToCall, objects: objectPairs.join(',') }});
      setCurrentCall(call);
      pendingCallContextRef.current = null;

      // Call event listeners
      call.on('ringing', () => {setCallStatus('ringing') });
      call.on('accept', () => { setCallStatus('active');});
      call.on('disconnect', () => { setCallStatus('ended'); setCurrentCall(null); setCallingDial(false); setCallingContactInfo(null); setIsMuted(false); setIsOnHold(false);
        // Refresh call logs
        dispatch({ type: 'GET_SOFTPHONE_CALL_LOGS', payload: { filterObj: { callType: { filteredValue: { in: ['cold_calling'] }}}, page: 1, limit: 50}  });
        // Refresh activities
        if (activeCallContextRef.current) {
          setTimeout(() => {
            dispatch({ type: 'GET_OBJECT_ACTIVITIES', payload: activeCallContextRef.current });
            activeCallContextRef.current = null;
          }, 2000);
        }
        setTimeout(() => setCallStatus('idle'), 2000);
      });
      call.on('cancel', () => { setCallStatus('ended'); setCurrentCall(null); setCallingDial(false); setCallingContactInfo(null); activeCallContextRef.current = null; });
      call.on('reject', () => { setCallStatus('ended'); setCurrentCall(null); setCallingDial(false); setCallingContactInfo(null); activeCallContextRef.current = null; });
    } catch (error) {
      console.error('Call failed:', error);
      setCallStatus('idle');
      setCallingDial(false);
    }
  }, [twilioDevice, deviceReady, code, contacts, pendingCall, callingContactInfo]);

  // Hang up current call
  const hangUpCall = useCallback(() => {
    if (currentCall) {
      console.log('Hanging up call');
      currentCall.disconnect();
      setCurrentCall(null);
      setCallStatus('idle');
      setCallingDial(false);
    }
  }, [currentCall]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (currentCall) {
      // If on hold, release hold first
      if (isOnHold) { currentCall.mute(false); setIsOnHold(false);}
      currentCall.mute(!isMuted);
      setIsMuted(!isMuted);
      if (callStatus === 'active' || callStatus === 'Muted' || callStatus === 'On Hold') setCallStatus(!isMuted ? 'Muted' : 'active');
    }
  }, [currentCall, isMuted, isOnHold, callStatus]);

  // Toggle hold
  const toggleHold = useCallback(() => {
    if (currentCall) {
      // If muted, unmute first
      if (isMuted) { currentCall.mute(false); setIsMuted(false);}
      currentCall.mute(!isOnHold);
      setIsOnHold(!isOnHold);
      if (callStatus === 'active' || callStatus === 'Muted' || callStatus === 'On Hold') setCallStatus(!isOnHold ? 'On Hold' : 'active');
    }
  }, [currentCall, isOnHold, isMuted, callStatus]);

  // ✅ Keyboard support (fixed)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Skip keyboard handler if user is typing in an input field to prevent duplication
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) return;
      
      const { key } = event;
      // Allow digits, *, #, and backspace
      if (/^[0-9*#]$/.test(key)) {
        // Special rule: pressing '1' adds both '11'
        if (key === '1') {  handleDigitClick('1')(); handleDigitClick('1')();}
        else handleDigitClick(key)()
      } else if (key === 'Backspace') handleBackspaceClick();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown) };
  }, [handleDigitClick, handleBackspaceClick]);

  const handleTabsSetCustomVoice = (event, newValue) => { 
    setValueTabUsing(newValue);
  };
  const handleTabsSetMe = (event, newValue) => { setValueMeTab(newValue);};

  return (
    <div className="dial-popup">
      <div className="header-title-phone">
        <h3>Calling From</h3>
        <Select
          value={twilioPhoneNumber || ''}
          onChange={(e) => setSelected(e.target.value)}
          displayEmpty
          size="small"
          disabled={!twilioPhoneNumber}
        >
          {twilioPhoneNumber ? (
            <MenuItem value={twilioPhoneNumber}>{twilioPhoneNumber}</MenuItem>
          ) : (
            <MenuItem value="" disabled>No phone number available</MenuItem>
          )}
        </Select>
        <button className="close-btn-dial" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDialPadShow(false); }}>x</button>
      </div>

      {/* Show Twilio integration error */}
      {twilioError ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', padding: '20px', flexDirection: 'column', textAlign: 'center' }}>
          <div style={{ color: '#856404', background: '#fff3cd', padding: '20px', borderRadius: '8px', border: '1px solid #ffc107' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>{twilioError}</p>
          </div>
        </div>
      ) : (
        <>
        <div className='tabs-content'>
        <TabPanel value={valueTabUsing} index={0}>
          <div className="dial-pad" ref={valueTabUsing === 0 ? scrollContainerRef : null}>
            <div className='searchbox'>
              <label>Recents</label>
              <div className="group-form">
                <i class='icon'><svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5.40156 10.6464C2.74292 10.6464 0.578369 8.48183 0.578369 5.82319C0.578369 3.16455 2.74292 1 5.40156 1C8.0602 1 10.2248 3.16455 10.2248 5.82319C10.2248 8.48183 8.0602 10.6464 5.40156 10.6464ZM5.40156 1.70583C3.12878 1.70583 1.2842 3.55511 1.2842 5.82319C1.2842 8.09127 3.12878 9.94055 5.40156 9.94055C7.67434 9.94055 9.51892 8.09127 9.51892 5.82319C9.51892 3.55511 7.67434 1.70583 5.40156 1.70583Z' fill='#50525870' stroke='#50525870' stroke-width='0.236842' /><path d='M12.8959 13.7066C12.8021 13.7068 12.7083 13.6723 12.6342 13.5984L8.68189 9.63155C8.5386 9.48864 8.53829 9.2518 8.68119 9.10852C8.82409 8.96524 9.06094 8.96492 9.20422 9.10783L13.1565 13.0747C13.2998 13.2176 13.3001 13.4545 13.1572 13.5977C13.0833 13.6718 12.9896 13.7065 12.8959 13.7066Z' fill='#50525870' stroke='#50525870' stroke-width='0.236842' /></svg></i>
                <TextField
                  fullWidth
                  hiddenLabel
                  size='small'
                  name='search'
                  value={contactSearch}
                  variant='standard'
                  placeholder='Search for recents'
                  onChange={(e) => setContactSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="recents-area style-two">
              {loading && currentPage === 1 ? (
                <div className="loading-call-logs">Loading call logs...</div>
              ) : filteredCallLogs.length > 0 ? (
                filteredCallLogs.map(log => (
                  <div key={log._id} className={`recent-box ${log.callType === 'cold_calling' ? 'outgoing' : ''} clickable-call-log`} onClick={() => handleCallLogClick(log)}>
                    <div className="left-area">
                      <i className="icon">{getCallLogInitials(log)}</i>
                      <div className="text-box">
                        <h4>{getCallLogContactName(log)}</h4>
                        <p>
                          <i className="icon">
                            {log.callType === 'cold_calling' ? (
                              <svg fill="currentColor" width="20px" height="20px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                <path d="M30.695 1.776l-0.001-0.013-0.002-0.002c-0.056-0.121-0.139-0.221-0.243-0.296l-0.002-0.002c-0.022-0.017-0.046-0.034-0.072-0.049l-0.003-0.002c-0.105-0.068-0.233-0.109-0.371-0.113l-0.001-0h-7.070c-0.414 0-0.75 0.336-0.75 0.75s0.336 0.75 0.75 0.75h5.26l-8.621 8.62c-0.136 0.136-0.219 0.323-0.219 0.53 0 0.415 0.336 0.751 0.751 0.751 0.208 0 0.395-0.084 0.531-0.22v0l8.619-8.619v5.26c0 0.414 0.336 0.75 0.75 0.75s0.75-0.336 0.75-0.75v0-7.071c-0.001-0.099-0.021-0.193-0.057-0.279l0.002 0.005zM29.996 21.352c-0.114-0.186-0.301-0.317-0.521-0.353l-0.004-0.001-8.969-1.424c-0.035-0.006-0.076-0.009-0.117-0.009-0.207 0-0.395 0.083-0.531 0.218l0-0c-0.676 0.68-1.194 1.516-1.496 2.451l-0.012 0.044c-4.016-1.640-7.141-4.765-8.742-8.675l-0.038-0.105c0.978-0.314 1.814-0.833 2.493-1.509l-0 0c0.136-0.136 0.22-0.324 0.22-0.531 0-0.041-0.003-0.081-0.010-0.12l0.001 0.004-1.425-8.969c-0.036-0.224-0.167-0.412-0.35-0.524l-0.003-0.002c-0.505-0.301-1.094-0.522-1.724-0.626l-0.029-0.004c-0.315-0.070-0.677-0.111-1.048-0.111-0.025 0-0.050 0-0.075 0.001l0.004-0h-0.006c-3.497 0.024-6.326 2.855-6.347 6.351v0.002c0.015 12.761 10.355 23.102 23.115 23.117h0.002c3.5-0.023 6.331-2.854 6.354-6.351v-0.002c0-0.020 0-0.044 0-0.068 0-0.356-0.036-0.703-0.106-1.038l0.006 0.033c-0.109-0.675-0.334-1.281-0.654-1.823l0.013 0.024zM24.383 29.076c-11.933-0.014-21.603-9.684-21.616-21.616v-0.001c0.019-2.673 2.182-4.835 4.854-4.853h0.002c0.016-0 0.036-0 0.055-0 0.272 0 0.537 0.030 0.793 0.086l-0.024-0.005c0.366 0.060 0.695 0.161 1.003 0.3l-0.025-0.010 1.302 8.202c-0.628 0.528-1.404 0.901-2.257 1.050l-0.029 0.004c-0.355 0.064-0.62 0.37-0.62 0.739 0 0.088 0.015 0.172 0.043 0.250l-0.002-0.005c1.772 5.072 5.695 8.994 10.646 10.729l0.121 0.037c0.073 0.026 0.157 0.041 0.245 0.041 0.368 0 0.674-0.265 0.737-0.615l0.001-0.005c0.153-0.882 0.526-1.658 1.061-2.295l-0.006 0.007 8.201 1.303c0.133 0.294 0.237 0.636 0.296 0.994l0.003 0.024c0.046 0.219 0.073 0.471 0.073 0.729 0 0.018-0 0.035-0 0.053l0-0.003c-0.016 2.675-2.179 4.840-4.852 4.859h-0.002z"></path>
                              </svg>
                            ) : (
                              <svg fill="currentColor" width="20px" height="20px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19.406 12.237l0.002 0.002c0.056 0.121 0.139 0.221 0.243 0.296l0.002 0.002c0.022 0.017 0.046 0.034 0.072 0.049l0.003 0.002c0.105 0.068 0.233 0.109 0.371 0.113l0.001 0h7.072c0.414 0 0.75-0.336 0.75-0.75s-0.336-0.75-0.75-0.75v0h-5.262l8.621-8.62c0.136-0.136 0.219-0.323 0.219-0.53 0-0.415-0.336-0.751-0.751-0.751-0.208 0-0.395 0.084-0.531 0.22v0l-8.619 8.619v-5.26c0-0.414-0.336-0.75-0.75-0.75s-0.75 0.336-0.75 0.75v0 7.071c0.001 0.099 0.021 0.193 0.057 0.279l-0.002-0.005zM29.996 21.352c-0.114-0.186-0.301-0.317-0.521-0.353l-0.004-0.001-8.969-1.424c-0.035-0.006-0.076-0.009-0.117-0.009-0.207 0-0.395 0.083-0.531 0.218l0-0c-0.676 0.68-1.194 1.516-1.496 2.451l-0.012 0.044c-4.016-1.640-7.141-4.765-8.742-8.675l-0.038-0.105c0.978-0.314 1.814-0.833 2.493-1.509l-0 0c0.136-0.136 0.22-0.324 0.22-0.531 0-0.041-0.003-0.081-0.010-0.12l0.001 0.004-1.425-8.969c-0.036-0.224-0.167-0.412-0.35-0.524l-0.003-0.002c-0.505-0.301-1.094-0.522-1.724-0.626l-0.029-0.004c-0.315-0.070-0.677-0.111-1.048-0.111-0.025 0-0.050 0-0.075 0.001l0.004-0h-0.006c-3.497 0.024-6.326 2.855-6.347 6.351v0.002c0.015 12.761 10.355 23.102 23.115 23.117h0.002c3.5-0.023 6.331-2.854 6.354-6.351v-0.002c0-0.020 0-0.044 0-0.068 0-0.356-0.036-0.703-0.106-1.038l0.006 0.033c-0.109-0.675-0.334-1.281-0.654-1.823l0.013 0.024zM24.383 29.076c-11.933-0.014-21.603-9.684-21.616-21.616v-0.001c0.019-2.673 2.182-4.835 4.854-4.853h0.002c0.016-0 0.036-0 0.055-0 0.272 0 0.537 0.030 0.793 0.086l-0.024-0.005c0.366 0.060 0.695 0.161 1.003 0.300l-0.025-0.010 1.302 8.202c-0.628 0.528-1.404 0.901-2.257 1.050l-0.029 0.004c-0.355 0.064-0.62 0.37-0.62 0.739 0 0.088 0.015 0.172 0.043 0.250l-0.002-0.005c1.772 5.072 5.695 8.994 10.646 10.729l0.121 0.037c0.073 0.026 0.157 0.041 0.245 0.041 0.368 0 0.674-0.265 0.737-0.615l0.001-0.005c0.153-0.882 0.526-1.658 1.061-2.295l-0.006 0.007 8.201 1.303c0.133 0.294 0.237 0.636 0.296 0.994l0.003 0.024c0.046 0.219 0.073 0.471 0.073 0.729 0 0.018-0 0.035-0 0.053l0-0.003c-0.016 2.675-2.179 4.840-4.852 4.859h-0.002z"></path>
                              </svg>
                            )}
                          </i>
                          {log.toNumber}
                        </p>
                      </div>
                    </div>
                    <div className="right-area">
                      <p>{formatRelativeTime(log.createdAt)}</p>
                      {getContactFromLog(log) && (
                        <button
                          className="contact-activity-btn"
                          onClick={(e) => { e.stopPropagation(); getContactFromLog(log, true); }}
                          title="View in contact activity"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-recent-calls">No recent calls</div>
              )}
              {valueTabUsing === 0 && filteredCallLogs.length > 0 && hasMore && (
                <div ref={scrollObserverRef} className="scroll-observer"></div>
              )}
            </div>

          </div>
        </TabPanel>
        <TabPanel value={valueTabUsing} index={1}>
          <div className="dial-pad" ref={valueTabUsing === 1 ? scrollContainerRef : null}>
            <div className='searchbox'>
              <label>Contacts</label>
              <div className="group-form">
                <i class='icon'><svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5.40156 10.6464C2.74292 10.6464 0.578369 8.48183 0.578369 5.82319C0.578369 3.16455 2.74292 1 5.40156 1C8.0602 1 10.2248 3.16455 10.2248 5.82319C10.2248 8.48183 8.0602 10.6464 5.40156 10.6464ZM5.40156 1.70583C3.12878 1.70583 1.2842 3.55511 1.2842 5.82319C1.2842 8.09127 3.12878 9.94055 5.40156 9.94055C7.67434 9.94055 9.51892 8.09127 9.51892 5.82319C9.51892 3.55511 7.67434 1.70583 5.40156 1.70583Z' fill='#50525870' stroke='#50525870' stroke-width='0.236842' /><path d='M12.8959 13.7066C12.8021 13.7068 12.7083 13.6723 12.6342 13.5984L8.68189 9.63155C8.5386 9.48864 8.53829 9.2518 8.68119 9.10852C8.82409 8.96524 9.06094 8.96492 9.20422 9.10783L13.1565 13.0747C13.2998 13.2176 13.3001 13.4545 13.1572 13.5977C13.0833 13.6718 12.9896 13.7065 12.8959 13.7066Z' fill='#50525870' stroke='#50525870' stroke-width='0.236842' /></svg></i>
                <TextField
                  fullWidth
                  hiddenLabel
                  size='small'
                  name='search'
                  value={contactSearch}
                  variant='standard'
                  placeholder='Search for contacts'
                  onChange={(e) => setContactSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="recents-area">
              {contactsLoading && currentPage === 1 ? (
                <div className="no-data">
                  <p>Loading contacts...</p>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="no-data">
                  <p>No contacts found</p>
                </div>
              ) : (
                filteredContacts.map((contact) => (
                  <div className="recent-box" key={contact._id}>
                    <div className="left-area clickable-contact" onClick={() => navigateToContact(contact._id)} style={{ cursor: 'pointer' }}>
                      <i className="icon">{getContactInitials(contact)}</i>
                      <div className="text-box">
                        <h4>{contact.firstName || ''} {contact.lastName || ''}</h4>
                        <p>{contact.phone || ''}</p>
                      </div>
                    </div>
                    {contact.phone && (
                      <button className="phone-btn" onClick={() => handleContactPhoneClick(contact.phone, contact)}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0875 17.0625C12.24 17.0625 11.3475 16.86 10.425 16.47C9.525 16.0875 8.6175 15.5625 7.7325 14.925C6.855 14.28 6.0075 13.56 5.205 12.7725C4.41 11.97 3.69 11.1225 3.0525 10.2525C2.4075 9.3525 1.89 8.4525 1.5225 7.5825C1.1325 6.6525 0.9375 5.7525 0.9375 4.905C0.9375 4.32 1.0425 3.765 1.245 3.2475C1.455 2.715 1.7925 2.22 2.25 1.7925C2.8275 1.2225 3.4875 0.9375 4.1925 0.9375C4.485 0.9375 4.785 1.005 5.04 1.125C5.3325 1.26 5.58 1.4625 5.76 1.7325L7.5 4.185C7.6575 4.4025 7.7775 4.6125 7.86 4.8225C7.9575 5.0475 8.01 5.2725 8.01 5.49C8.01 5.775 7.9275 6.0525 7.77 6.315C7.6575 6.5175 7.485 6.735 7.2675 6.9525L6.7575 7.485C6.765 7.5075 6.7725 7.5225 6.78 7.5375C6.87 7.695 7.05 7.965 7.395 8.37C7.7625 8.79 8.1075 9.1725 8.4525 9.525C8.895 9.96 9.2625 10.305 9.6075 10.59C10.035 10.95 10.3125 11.13 10.4775 11.2125L10.4625 11.25L11.01 10.71C11.2425 10.4775 11.4675 10.305 11.685 10.1925C12.0975 9.9375 12.6225 9.8925 13.1475 10.11C13.3425 10.1925 13.5525 10.305 13.7775 10.4625L16.2675 12.2325C16.545 12.42 16.7475 12.66 16.8675 12.945C16.98 13.23 17.0325 13.4925 17.0325 13.755C17.0325 14.115 16.95 14.475 16.7925 14.8125C16.635 15.15 16.44 15.4425 16.1925 15.7125C15.765 16.185 15.3 16.5225 14.76 16.74C14.2425 16.95 13.68 17.0625 13.0875 17.0625ZM4.1925 2.0625C3.78 2.0625 3.3975 2.2425 3.03 2.6025C2.685 2.925 2.445 3.2775 2.295 3.66C2.1375 4.05 2.0625 4.4625 2.0625 4.905C2.0625 5.6025 2.2275 6.36 2.5575 7.14C2.895 7.935 3.3675 8.76 3.9675 9.585C4.5675 10.41 5.25 11.2125 6 11.97C6.75 12.7125 7.56 13.4025 8.3925 14.01C9.2025 14.6025 10.035 15.0825 10.86 15.4275C12.1425 15.975 13.3425 16.1025 14.3325 15.69C14.715 15.5325 15.0525 15.2925 15.36 14.9475C15.5325 14.76 15.6675 14.5575 15.78 14.3175C15.87 14.13 15.915 13.935 15.915 13.74C15.915 13.62 15.8925 13.5 15.8325 13.365C15.81 13.32 15.765 13.2375 15.6225 13.14L13.1325 11.37C12.9825 11.265 12.8475 11.19 12.72 11.1375C12.555 11.07 12.4875 11.0025 12.2325 11.16C12.0825 11.235 11.9475 11.3475 11.7975 11.4975L11.2275 12.06C10.935 12.345 10.485 12.4125 10.14 12.285L9.9375 12.195C9.63 12.03 9.27 11.775 8.8725 11.4375C8.5125 11.13 8.1225 10.77 7.65 10.305C7.2825 9.93 6.915 9.5325 6.5325 9.09C6.18 8.6775 5.925 8.325 5.7675 8.0325L5.6775 7.8075C5.6325 7.635 5.6175 7.5375 5.6175 7.4325C5.6175 7.1625 5.715 6.9225 5.9025 6.735L6.465 6.15C6.615 6 6.7275 5.8575 6.8025 5.73C6.8625 5.6325 6.885 5.55 6.885 5.475C6.885 5.415 6.8625 5.325 6.825 5.235C6.7725 5.115 6.69 4.98 6.585 4.8375L4.845 2.3775C4.77 2.2725 4.68 2.1975 4.5675 2.145C4.4475 2.0925 4.32 2.0625 4.1925 2.0625ZM10.4625 11.2575L10.3425 11.7675L10.545 11.2425C10.5075 11.235 10.4775 11.2425 10.4625 11.2575Z" fill="#3A3D44"></path></svg>
                      </button>
                    )}
                  </div>
                ))
              )}
              {valueTabUsing === 1 && filteredContacts.length > 0 && hasMore && (
                <div ref={scrollObserverRef} className="scroll-observer"></div>
              )}
            </div>
          </div>
        </TabPanel>
        <TabPanel value={valueTabUsing} index={2}>
          <div className="dial-pad" data-testid="dial-pad">
            {callingDial
              ? <div className="dialpad-area style-active">
                <div className="time-area">
                  <p>
                    <i className="icon">
                      <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 100 100"><g><path d="M28.1,48C28,48.7,28,49.3,28,50h-6c0-0.7,0-1.3,0.1-2H28.1z"></path></g><g><g><path d="M51.5,36h-3c-0.8,0-1.5,0.7-1.5,1.5v13.1c0,0.4,0.2,0.8,0.4,1.1l8.4,8.4c0.6,0.6,1.5,0.6,2.1,0l2.1-2.1
                          c0.6-0.6,0.6-1.5,0-2.1L53,48.8V37.5C53,36.7,52.3,36,51.5,36z"></path></g><g><path d="M50,22c-14.8,0-26.9,11.5-27.9,26c0,0.3-0.1,0.7-0.1,1h-4.5c-1.3,0-2,1.5-1.2,2.4l7.5,9.1
                          c0.6,0.7,1.7,0.7,2.3,0l7.5-9.1c0.8-1,0.1-2.4-1.2-2.4H28c0-0.3,0-0.7,0-1c1-11.2,10.5-20,21.9-20c13,0,23.3,11.3,21.9,24.5
                          C70.8,62,61.8,71,52.2,71.9c-7.1,0.7-13.8-1.9-18.5-7c-0.6-0.7-1.4-1.1-2.2-0.1l-2.4,2.9c-0.5,0.6-0.1,1,0.4,1.5
                          c5.4,5.7,12.8,8.9,20.8,8.8c14.4-0.2,26.5-11.6,27.5-26C79.1,35.7,66.1,22,50,22z"></path></g></g></svg>
                    </i>
                    {getLocalTimeForPhoneNumber(callingContactInfo?.phone)?.fullText || moment().format('h:mm A [local Time]')}
                  </p>
                </div>
                <div className="calling-active">
                  <div className="icon">
                    {callingContactInfo?.initials || 'U'}
                  </div>
                  <h4>
                    {callingContactInfo?.name && callingContactInfo.name !== 'Unknown' && (
                      <i className="icon-card">
                        <svg width="30px" height="30px" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M14.5 11.5C14.5 12.6046 13.6046 13.5 12.5 13.5C11.3954 13.5 10.5 12.6046 10.5 11.5C10.5 10.3954 11.3954 9.5 12.5 9.5C13.6046 9.5 14.5 10.3954 14.5 11.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M14.5 5.25C14.0858 5.25 13.75 5.58579 13.75 6C13.75 6.41421 14.0858 6.75 14.5 6.75V5.25ZM10.5 6.75C10.9142 6.75 11.25 6.41421 11.25 6C11.25 5.58579 10.9142 5.25 10.5 5.25V6.75ZM15.25 6C15.25 5.58579 14.9142 5.25 14.5 5.25C14.0858 5.25 13.75 5.58579 13.75 6H15.25ZM13.75 7C13.75 7.41421 14.0858 7.75 14.5 7.75C14.9142 7.75 15.25 7.41421 15.25 7H13.75ZM13.75 6C13.75 6.41421 14.0858 6.75 14.5 6.75C14.9142 6.75 15.25 6.41421 15.25 6H13.75ZM15.25 4C15.25 3.58579 14.9142 3.25 14.5 3.25C14.0858 3.25 13.75 3.58579 13.75 4H15.25ZM14.5 6.75C14.9142 6.75 15.25 6.41421 15.25 6C15.25 5.58579 14.9142 5.25 14.5 5.25V6.75ZM10.5 5.25C10.0858 5.25 9.75 5.58579 9.75 6C9.75 6.41421 10.0858 6.75 10.5 6.75V5.25ZM11.25 6C11.25 5.58579 10.9142 5.25 10.5 5.25C10.0858 5.25 9.75 5.58579 9.75 6H11.25ZM9.75 7C9.75 7.41421 10.0858 7.75 10.5 7.75C10.9142 7.75 11.25 7.41421 11.25 7H9.75ZM9.75 6C9.75 6.41421 10.0858 6.75 10.5 6.75C10.9142 6.75 11.25 6.41421 11.25 6H9.75ZM11.25 4C11.25 3.58579 10.9142 3.25 10.5 3.25C10.0858 3.25 9.75 3.58579 9.75 4H11.25ZM6.05108 17.8992C5.71926 18.1471 5.65126 18.6171 5.89919 18.9489C6.14713 19.2807 6.61711 19.3487 6.94892 19.1008L6.05108 17.8992ZM18.0511 19.1008C18.3829 19.3487 18.8529 19.2807 19.1008 18.9489C19.3487 18.6171 19.2807 18.1471 18.9489 17.8992L18.0511 19.1008ZM14.5 6.75H15.5V5.25H14.5V6.75ZM15.5 6.75C17.2949 6.75 18.75 8.20507 18.75 10H20.25C20.25 7.37665 18.1234 5.25 15.5 5.25V6.75ZM18.75 10V16H20.25V10H18.75ZM18.75 16C18.75 17.7949 17.2949 19.25 15.5 19.25V20.75C18.1234 20.75 20.25 18.6234 20.25 16H18.75ZM15.5 19.25H9.5V20.75H15.5V19.25ZM9.5 19.25C7.70507 19.25 6.25 17.7949 6.25 16H4.75C4.75 18.6234 6.87665 20.75 9.5 20.75V19.25ZM6.25 16V10H4.75V16H6.25ZM6.25 10C6.25 8.20507 7.70507 6.75 9.5 6.75V5.25C6.87665 5.25 4.75 7.37665 4.75 10H6.25ZM9.5 6.75H10.5V5.25H9.5V6.75ZM13.75 6V7H15.25V6H13.75ZM15.25 6V4H13.75V6H15.25ZM14.5 5.25H10.5V6.75H14.5V5.25ZM9.75 6V7H11.25V6H9.75ZM11.25 6V4H9.75V6H11.25ZM6.94892 19.1008C10.2409 16.641 14.7591 16.641 18.0511 19.1008L18.9489 17.8992C15.1245 15.0416 9.87551 15.0416 6.05108 17.8992L6.94892 19.1008Z" fill="currentColor"></path> </g></svg>
                      </i>
                    )}
                    {callingContactInfo?.name || ''}
                  </h4>
                  <p>{callingContactInfo?.phone || ''}</p>
                  <p>{getCallStatusText()}</p>
                </div>

                {/* <div className="details-area">
                  <button className="details-btn">
                    <i className="icon-btn">
                      <svg fill="currentColor" height="800px" width="800px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 217.762 217.762" stroke="currentColor">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M108.881,5.334C48.844,5.334,0,45.339,0,94.512c0,28.976,16.84,55.715,45.332,72.454 c-3.953,18.48-12.812,31.448-12.909,31.588l-9.685,13.873l16.798-2.153c1.935-0.249,47.001-6.222,79.122-26.942 c26.378-1.92,50.877-11.597,69.181-27.364c19.296-16.623,29.923-38.448,29.923-61.455C217.762,45.339,168.918,5.334,108.881,5.334z M115.762,168.489l-2.049,0.117l-1.704,1.145c-18.679,12.548-43.685,19.509-59.416,22.913c3.3-7.377,6.768-17.184,8.499-28.506 l0.809-5.292l-4.741-2.485C30.761,142.547,15,119.42,15,94.512c0-40.901,42.115-74.178,93.881-74.178s93.881,33.276,93.881,74.178 C202.762,133.194,164.547,165.688,115.762,168.489z" /> </g>
                      </svg>
                    </i>
                    Message
                  </button>
                  <button className="details-btn">
                    <i className="icon-btn">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M16 3.98999H8C6.93913 3.98999 5.92178 4.41135 5.17163 5.1615C4.42149 5.91164 4 6.92912 4 7.98999V17.99C4 19.0509 4.42149 20.0682 5.17163 20.8184C5.92178 21.5685 6.93913 21.99 8 21.99H16C17.0609 21.99 18.0783 21.5685 18.8284 20.8184C19.5786 20.0682 20 19.0509 20 17.99V7.98999C20 6.92912 19.5786 5.91164 18.8284 5.1615C18.0783 4.41135 17.0609 3.98999 16 3.98999Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M9 2V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M15 2V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> </g>
                      </svg>
                    </i>
                    Notes
                  </button>
                  <button className="details-btn disabled-btn two-line-text">
                    <i className="icon-btn">
                      <svg width="800px" height="800px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#000000" class="bi bi-telephone-inbound"><path d="M15.854.146a.5.5 0 0 1 0 .708L11.707 5H14.5a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 1 0v2.793L15.146.146a.5.5 0 0 1 .708 0zm-12.2 1.182a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"></path></svg>
                    </i>
                    Blind<br />Tranfser
                  </button>
                  <button className="details-btn disabled-btn two-line-text">
                    <i className="icon-btn">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M21.97 18.33C21.97 18.69 21.89 19.06 21.72 19.42C21.55 19.78 21.33 20.12 21.04 20.44C20.55 20.98 20.01 21.37 19.4 21.62C18.8 21.87 18.15 22 17.45 22C16.43 22 15.34 21.76 14.19 21.27C13.04 20.78 11.89 20.12 10.75 19.29C9.6 18.45 8.51 17.52 7.47 16.49C6.44 15.45 5.51 14.36 4.68 13.22C3.86 12.08 3.2 10.94 2.72 9.81C2.24 8.67 2 7.58 2 6.54C2 5.86 2.12 5.21 2.36 4.61C2.6 4 2.98 3.44 3.51 2.94C4.15 2.31 4.85 2 5.59 2C5.87 2 6.15 2.06 6.4 2.18C6.66 2.3 6.89 2.48 7.07 2.74L9.39 6.01C9.57 6.26 9.7 6.49 9.79 6.71C9.88 6.92 9.93 7.13 9.93 7.32C9.93 7.56 9.86 7.8 9.72 8.03C9.59 8.26 9.4 8.5 9.16 8.74L8.4 9.53C8.29 9.64 8.24 9.77 8.24 9.93C8.24 10.01 8.25 10.08 8.27 10.16C8.3 10.24 8.33 10.3 8.35 10.36C8.53 10.69 8.84 11.12 9.28 11.64C9.73 12.16 10.21 12.69 10.73 13.22C11.27 13.75 11.79 14.24 12.32 14.69C12.84 15.13 13.27 15.43 13.61 15.61C13.66 15.63 13.72 15.66 13.79 15.69C13.87 15.72 13.95 15.73 14.04 15.73C14.21 15.73 14.34 15.67 14.45 15.56L15.21 14.81C15.46 14.56 15.7 14.37 15.93 14.25C16.16 14.11 16.39 14.04 16.64 14.04C16.83 14.04 17.03 14.08 17.25 14.17C17.47 14.26 17.7 14.39 17.95 14.56L21.26 16.91C21.52 17.09 21.7 17.3 21.81 17.55C21.91 17.8 21.97 18.05 21.97 18.33Z" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" /> <path d="M16.23 7.76998L19.77 4.22998" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M19.77 7.76998L16.23 4.22998" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> </g>
                      </svg>
                    </i>
                    Waram<br />Tranfser
                  </button>
                </div> */}
                <div className="details-area">
                  <button className={`details-btn ${isOnHold ? 'active' : ''} ${callStatus === 'ringing' ? 'disabled-btn' : ''}`} onClick={toggleHold} disabled={callStatus === 'ringing' || callStatus === 'connecting'}>
                    <i className="icon-btn">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M8 5V19M16 5V19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /> </g>
                      </svg>
                    </i>
                    Hold
                  </button>
                  <button className={`details-btn ${isMuted ? 'active' : ''} ${callStatus === 'ringing' ? 'disabled-btn' : ''}`} onClick={toggleMute} disabled={callStatus === 'ringing' || callStatus === 'connecting'}>
                    <i className="icon-btn">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M3 3L21 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M9 9V9C9 11.7614 11.2386 14 14 14V14M15 10.5V5C15 3.34315 13.6569 2 12 2V2C10.3431 2 9 3.34315 9 5V5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M5 10V11C5 14.866 8.13401 18 12 18V18V18C15.866 18 19 14.866 19 11V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M12 18V22M12 22H9M12 22H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> </g>
                      </svg>
                    </i>
                    Mute
                  </button>
                  {/* <button className="details-btn disabled-btn">
                    <i className="icon-btn">
                      <svg width="800px" height="800px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#000000" class="bi bi-telephone-inbound"><path d="M15.854.146a.5.5 0 0 1 0 .708L11.707 5H14.5a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 1 0v2.793L15.146.146a.5.5 0 0 1 .708 0zm-12.2 1.182a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"></path></svg>
                    </i>
                    Blind Transfer
                  </button> */}
                  <button className="details-btn" onClick={() => { setCallingDial(false); setCode(''); }}>
                    <i className="icon-btn">
                      <svg width="30px" height="30px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="currentColor" stroke="currentColor"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g id="icomoon-ignore"> </g> <path d="M6.937 21.865c-1.766 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM6.937 27.195c-1.176 0-2.132-0.956-2.132-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M6.937 3.738c-1.766 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM6.937 9.069c-1.176 0-2.132-0.956-2.132-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M6.937 12.779c-1.766 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM6.937 18.11c-1.176 0-2.132-0.957-2.132-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M16 21.865c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM16 27.195c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M16 3.738c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM16 9.069c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M16 12.779c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM16 18.11c-1.176 0-2.133-0.957-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M25.063 21.865c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM25.063 27.195c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M25.063 10.135c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199zM25.063 4.805c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132z" fill="currentColor"> </path> <path d="M25.063 12.779c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM25.063 18.11c-1.176 0-2.133-0.957-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> </g></svg>
                    </i>
                    Dial
                  </button>
                </div>

                <div className="controls" >
                  <button className="call" onClick={hangUpCall}>
                    <i className="icon-call">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M10.3201 18.97C10.4601 19.08 10.6001 19.18 10.7501 19.29C11.8901 20.12 13.0401 20.78 14.1901 21.27C15.3401 21.76 16.4301 22 17.4501 22C18.1501 22 18.8001 21.87 19.4001 21.62C20.0101 21.37 20.5501 20.98 21.0401 20.44C21.3301 20.12 21.5501 19.78 21.7201 19.42C21.8901 19.06 21.9701 18.69 21.9701 18.33C21.9701 18.05 21.9101 17.8 21.8101 17.55C21.7001 17.3 21.5201 17.09 21.2601 16.91L17.9501 14.56C17.7001 14.39 17.4701 14.26 17.2501 14.17C17.0301 14.08 16.8301 14.04 16.6401 14.04C16.3901 14.04 16.1601 14.11 15.9301 14.25C15.7001 14.37 15.4601 14.56 15.2101 14.81L14.4501 15.56C14.3401 15.67 14.2101 15.73 14.0401 15.73C13.9501 15.73 13.8701 15.72 13.7901 15.69C13.7201 15.66 13.6601 15.63 13.6101 15.61C13.4201 15.51 13.2001 15.37 12.9601 15.19" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" /> <path d="M10.73 13.22C10.21 12.69 9.73 12.16 9.28 11.64C8.84 11.12 8.53 10.69 8.35 10.36C8.33 10.3 8.3 10.24 8.27 10.16C8.25 10.08 8.24 10.01 8.24 9.93C8.24 9.77 8.29 9.64 8.4 9.53L9.16 8.74C9.4 8.5 9.59 8.26 9.72 8.03C9.86 7.8 9.93 7.56 9.93 7.32C9.93 7.13 9.88 6.92 9.79 6.71C9.7 6.49 9.57 6.26 9.39 6.01L7.07 2.74C6.89 2.48 6.66 2.3 6.4 2.18C6.15 2.06 5.87 2 5.59 2C4.85 2 4.15 2.31 3.51 2.94C2.98 3.44 2.6 4 2.36 4.61C2.12 5.21 2 5.86 2 6.54C2 7.58 2.24 8.67 2.72 9.81C3.2 10.94 3.86 12.08 4.68 13.22C5.51 14.36 6.44 15.45 7.47 16.49" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" /> <path d="M22 2L2 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> </g>
                      </svg>
                    </i>
                    End Call
                  </button>
                </div>
              </div>
              : <div className="dialpad-area">
                {currentCall && (callStatus === 'active' || callStatus === 'ringing' || callStatus === 'connecting') && (
                  <div className="back-to-call-btn" onClick={() => setCallingDial(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="back-to-call-text">Back to Call</span>
                  </div>
                )}
                <input
                  type="text"
                  className="phone-number"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />

                {matchedContacts.length > 0 && (
                  <div className="more-contact">
                    <div className="contact-number-btn">
                      <div className="left-area">
                        <i className="icon">
                          <svg width="30px" height="30px" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g id="SVGRepo_bgCarrier" strokeWidth="0" />
                            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
                            <g id="SVGRepo_iconCarrier"> <path fillRule="evenodd" clipRule="evenodd" d="M14.5 11.5C14.5 12.6046 13.6046 13.5 12.5 13.5C11.3954 13.5 10.5 12.6046 10.5 11.5C10.5 10.3954 11.3954 9.5 12.5 9.5C13.6046 9.5 14.5 10.3954 14.5 11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /> <path d="M14.5 5.25C14.0858 5.25 13.75 5.58579 13.75 6C13.75 6.41421 14.0858 6.75 14.5 6.75V5.25ZM10.5 6.75C10.9142 6.75 11.25 6.41421 11.25 6C11.25 5.58579 10.9142 5.25 10.5 5.25V6.75ZM15.25 6C15.25 5.58579 14.9142 5.25 14.5 5.25C14.0858 5.25 13.75 5.58579 13.75 6H15.25ZM13.75 7C13.75 7.41421 14.0858 7.75 14.5 7.75C14.9142 7.75 15.25 7.41421 15.25 7H13.75ZM13.75 6C13.75 6.41421 14.0858 6.75 14.5 6.75C14.9142 6.75 15.25 6.41421 15.25 6H13.75ZM15.25 4C15.25 3.58579 14.9142 3.25 14.5 3.25C14.0858 3.25 13.75 3.58579 13.75 4H15.25ZM14.5 6.75C14.9142 6.75 15.25 6.41421 15.25 6C15.25 5.58579 14.9142 5.25 14.5 5.25V6.75ZM10.5 5.25C10.0858 5.25 9.75 5.58579 9.75 6C9.75 6.41421 10.0858 6.75 10.5 6.75V5.25ZM11.25 6C11.25 5.58579 10.9142 5.25 10.5 5.25C10.0858 5.25 9.75 5.58579 9.75 6H11.25ZM9.75 7C9.75 7.41421 10.0858 7.75 10.5 7.75C10.9142 7.75 11.25 7.41421 11.25 7H9.75ZM9.75 6C9.75 6.41421 10.0858 6.75 10.5 6.75C10.9142 6.75 11.25 6.41421 11.25 6H9.75ZM11.25 4C11.25 3.58579 10.9142 3.25 10.5 3.25C10.0858 3.25 9.75 3.58579 9.75 4H11.25ZM6.05108 17.8992C5.71926 18.1471 5.65126 18.6171 5.89919 18.9489C6.14713 19.2807 6.61711 19.3487 6.94892 19.1008L6.05108 17.8992ZM18.0511 19.1008C18.3829 19.3487 18.8529 19.2807 19.1008 18.9489C19.3487 18.6171 19.2807 18.1471 18.9489 17.8992L18.0511 19.1008ZM14.5 6.75H15.5V5.25H14.5V6.75ZM15.5 6.75C17.2949 6.75 18.75 8.20507 18.75 10H20.25C20.25 7.37665 18.1234 5.25 15.5 5.25V6.75ZM18.75 10V16H20.25V10H18.75ZM18.75 16C18.75 17.7949 17.2949 19.25 15.5 19.25V20.75C18.1234 20.75 20.25 18.6234 20.25 16H18.75ZM15.5 19.25H9.5V20.75H15.5V19.25ZM9.5 19.25C7.70507 19.25 6.25 17.7949 6.25 16H4.75C4.75 18.6234 6.87665 20.75 9.5 20.75V19.25ZM6.25 16V10H4.75V16H6.25ZM6.25 10C6.25 8.20507 7.70507 6.75 9.5 6.75V5.25C6.87665 5.25 4.75 7.37665 4.75 10H6.25ZM9.5 6.75H10.5V5.25H9.5V6.75ZM13.75 6V7H15.25V6H13.75ZM15.25 6V4H13.75V6H15.25ZM14.5 5.25H10.5V6.75H14.5V5.25ZM9.75 6V7H11.25V6H9.75ZM11.25 6V4H9.75V6H11.25ZM6.94892 19.1008C10.2409 16.641 14.7591 16.641 18.0511 19.1008L18.9489 17.8992C15.1245 15.0416 9.87551 15.0416 6.05108 17.8992L6.94892 19.1008Z" fill="currentColor" /> </g>
                          </svg>
                        </i>
                        <p><span>{matchedContacts[0].firstName} {matchedContacts[0].lastName}</span> <span>{matchedContacts[0].phone}</span></p>
                      </div>
                      <button onClick={() => {
                        const phoneNumber = matchedContacts[0].phone;
                        setCode(phoneNumber);
                        makeCall(phoneNumber);
                      }}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0875 17.0625C12.24 17.0625 11.3475 16.86 10.425 16.47C9.525 16.0875 8.6175 15.5625 7.7325 14.925C6.855 14.28 6.0075 13.56 5.205 12.7725C4.41 11.97 3.69 11.1225 3.0525 10.2525C2.4075 9.3525 1.89 8.4525 1.5225 7.5825C1.1325 6.6525 0.9375 5.7525 0.9375 4.905C0.9375 4.32 1.0425 3.765 1.245 3.2475C1.455 2.715 1.7925 2.22 2.25 1.7925C2.8275 1.2225 3.4875 0.9375 4.1925 0.9375C4.485 0.9375 4.785 1.005 5.04 1.125C5.3325 1.26 5.58 1.4625 5.76 1.7325L7.5 4.185C7.6575 4.4025 7.7775 4.6125 7.86 4.8225C7.9575 5.0475 8.01 5.2725 8.01 5.49C8.01 5.775 7.9275 6.0525 7.77 6.315C7.6575 6.5175 7.485 6.735 7.2675 6.9525L6.7575 7.485C6.765 7.5075 6.7725 7.5225 6.78 7.5375C6.87 7.695 7.05 7.965 7.395 8.37C7.7625 8.79 8.1075 9.1725 8.4525 9.525C8.895 9.96 9.2625 10.305 9.6075 10.59C10.035 10.95 10.3125 11.13 10.4775 11.2125L10.4625 11.25L11.01 10.71C11.2425 10.4775 11.4675 10.305 11.685 10.1925C12.0975 9.9375 12.6225 9.8925 13.1475 10.11C13.3425 10.1925 13.5525 10.305 13.7775 10.4625L16.2675 12.2325C16.545 12.42 16.7475 12.66 16.8675 12.945C16.98 13.23 17.0325 13.4925 17.0325 13.755C17.0325 14.115 16.95 14.475 16.7925 14.8125C16.635 15.15 16.44 15.4425 16.1925 15.7125C15.765 16.185 15.3 16.5225 14.76 16.74C14.2425 16.95 13.68 17.0625 13.0875 17.0625ZM4.1925 2.0625C3.78 2.0625 3.3975 2.2425 3.03 2.6025C2.685 2.925 2.445 3.2775 2.295 3.66C2.1375 4.05 2.0625 4.4625 2.0625 4.905C2.0625 5.6025 2.2275 6.36 2.5575 7.14C2.895 7.935 3.3675 8.76 3.9675 9.585C4.5675 10.41 5.25 11.2125 6 11.97C6.75 12.7125 7.56 13.4025 8.3925 14.01C9.2025 14.6025 10.035 15.0825 10.86 15.4275C12.1425 15.975 13.3425 16.1025 14.3325 15.69C14.715 15.5325 15.0525 15.2925 15.36 14.9475C15.5325 14.76 15.6675 14.5575 15.78 14.3175C15.87 14.13 15.915 13.935 15.915 13.74C15.915 13.62 15.8925 13.5 15.8325 13.365C15.81 13.32 15.765 13.2375 15.6225 13.14L13.1325 11.37C12.9825 11.265 12.8475 11.19 12.72 11.1375C12.555 11.07 12.4875 11.0025 12.2325 11.16C12.0825 11.235 11.9475 11.3475 11.7975 11.4975L11.2275 12.06C10.935 12.345 10.485 12.4125 10.14 12.285L9.9375 12.195C9.63 12.03 9.27 11.775 8.8725 11.4375C8.5125 11.13 8.1225 10.77 7.65 10.305C7.2825 9.93 6.915 9.5325 6.5325 9.09C6.18 8.6775 5.925 8.325 5.7675 8.0325L5.6775 7.8075C5.6325 7.635 5.6175 7.5375 5.6175 7.4325C5.6175 7.1625 5.715 6.9225 5.9025 6.735L6.465 6.15C6.615 6 6.7275 5.8575 6.8025 5.73C6.8625 5.6325 6.885 5.55 6.885 5.475C6.885 5.415 6.8625 5.325 6.825 5.235C6.7725 5.115 6.69 4.98 6.585 4.8375L4.845 2.3775C4.77 2.2725 4.68 2.1975 4.5675 2.145C4.4475 2.0925 4.32 2.0625 4.1925 2.0625ZM10.4625 11.2575L10.3425 11.7675L10.545 11.2425C10.5075 11.235 10.4775 11.2425 10.4625 11.2575Z" fill="#3A3D44"></path></svg>
                      </button>
                    </div>
                    <div className="contact-number-btn style-two clickable-contact-btn" onClick={() => setValueTabUsing(1)}>
                      <div className="left-area">
                        <i className="icon fa fa-users"></i>
                        <p>More Contacts</p>
                      </div>
                      <button>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </div>
                  </div>
                )}

                <div className="digits">
                  {[['1'],['2'],['3'],['4'],['5'],['6'],['7'],['8'],['9'],['*'],['0'],['#']]
                  .map(([num, letters]) => (
                    <div key={num} className="digit" onClick={handleDigitClick(num)}> {num} </div>
                  ))}
                </div>

                <div className="controls">
                  <div className="call" onClick={makeCall}>
                    <CallIcon />
                  </div>
                  <div className="backspace" onClick={handleBackspaceClick}>
                    <BackspaceIcon />
                  </div>
                </div>
              </div>
            }


          </div>
        </TabPanel>
        <TabPanel value={valueTabUsing} index={3}>
          <div className="dial-pad" >
            <div className='searchbox'>
              <div className="group-form">
                <i class='icon'><svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5.40156 10.6464C2.74292 10.6464 0.578369 8.48183 0.578369 5.82319C0.578369 3.16455 2.74292 1 5.40156 1C8.0602 1 10.2248 3.16455 10.2248 5.82319C10.2248 8.48183 8.0602 10.6464 5.40156 10.6464ZM5.40156 1.70583C3.12878 1.70583 1.2842 3.55511 1.2842 5.82319C1.2842 8.09127 3.12878 9.94055 5.40156 9.94055C7.67434 9.94055 9.51892 8.09127 9.51892 5.82319C9.51892 3.55511 7.67434 1.70583 5.40156 1.70583Z' fill='#50525870' stroke='#50525870' stroke-width='0.236842' /><path d='M12.8959 13.7066C12.8021 13.7068 12.7083 13.6723 12.6342 13.5984L8.68189 9.63155C8.5386 9.48864 8.53829 9.2518 8.68119 9.10852C8.82409 8.96524 9.06094 8.96492 9.20422 9.10783L13.1565 13.0747C13.2998 13.2176 13.3001 13.4545 13.1572 13.5977C13.0833 13.6718 12.9896 13.7065 12.8959 13.7066Z' fill='#50525870' stroke='#50525870' stroke-width='0.236842' /></svg></i>
                <TextField
                  autoFocus // <-- optional, for safety
                  fullWidth
                  hiddenLabel
                  size='small'
                  name='search'
                  // value={searchValue}
                  variant='standard'
                  placeholder='Search for phone number'
                // onChange={onSearchChange}
                />
              </div>
            </div>
            <div className="recents-area">
              <div className="tabs-under">
                <div className="bottom-footer">
                  <div className='tabs-box'>
                    <AppBar position="static" color="default">
                      <Tabs
                        value={valueMeTab}
                        onChange={handleTabsSetMe}
                        aria-label="simple tabs example"
                        variant="fullWidth"
                      >
                        <Tab label="For Me" />
                        <Tab label="All" />
                      </Tabs>
                    </AppBar>
                  </div>
                </div>
                <div className='tabs-content'>
                  <TabPanel value={valueMeTab} index={0}>
                    <div className="recents-area">
                      <div className="no-data">
                        <i className="icon">
                          <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                            <g id="SVGRepo_bgCarrier" stroke-width="0" />
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                            <g id="SVGRepo_iconCarrier"> <path d="M6 16C8.20914 16 10 14.2091 10 12C10 9.79086 8.20914 8 6 8C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16ZM6 16H18M18 16C20.2091 16 22 14.2091 22 12C22 9.79086 20.2091 8 18 8C15.7909 8 14 9.79086 14 12C14 14.2091 15.7909 16 18 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /> </g>
                          </svg>
                        </i>
                        <h4>No Voicemail</h4>
                      </div>
                    </div>
                  </TabPanel>
                  <TabPanel value={valueMeTab} index={1}>
                    <div className="recents-area">
                      <div className="no-data">
                        <i className="icon">
                          <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                            <g id="SVGRepo_bgCarrier" stroke-width="0" />
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                            <g id="SVGRepo_iconCarrier"> <path d="M6 16C8.20914 16 10 14.2091 10 12C10 9.79086 8.20914 8 6 8C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16ZM6 16H18M18 16C20.2091 16 22 14.2091 22 12C22 9.79086 20.2091 8 18 8C15.7909 8 14 9.79086 14 12C14 14.2091 15.7909 16 18 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /> </g>
                          </svg>
                        </i>
                        <h4>No Voicemail</h4>
                      </div>
                    </div>
                  </TabPanel>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        {/* AI Calling Tab */}
        <TabPanel value={valueTabUsing} index={4}>
          <div className="dial-pad">
            <MakeCall 
              clearActivityForm={() => {}} 
              onCreateActivity={() => {}} 
              isActiveExpande={false} 
              setIsActiveExpande={() => {}} 
              isInDialpad={true}
            />
          </div>
        </TabPanel>
      </div>

      <div className="bottom-footer">
        <div className='tabs-box'>
          <AppBar position="static" color="default">
            <Tabs
              value={valueTabUsing}
              onChange={handleTabsSetCustomVoice}
              aria-label="simple tabs example"
              variant="fullWidth"
            >
              <Tab icon={<i className="icon">
                <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 100 100">
                  <g>
                    <path d="M28.1,48C28,48.7,28,49.3,28,50h-6c0-0.7,0-1.3,0.1-2H28.1z" />
                  </g>
                  <g>
                    <g>
                      <path d="M51.5,36h-3c-0.8,0-1.5,0.7-1.5,1.5v13.1c0,0.4,0.2,0.8,0.4,1.1l8.4,8.4c0.6,0.6,1.5,0.6,2.1,0l2.1-2.1
			                          c0.6-0.6,0.6-1.5,0-2.1L53,48.8V37.5C53,36.7,52.3,36,51.5,36z"/>
                    </g>
                    <g>
                      <path d="M50,22c-14.8,0-26.9,11.5-27.9,26c0,0.3-0.1,0.7-0.1,1h-4.5c-1.3,0-2,1.5-1.2,2.4l7.5,9.1
                                c0.6,0.7,1.7,0.7,2.3,0l7.5-9.1c0.8-1,0.1-2.4-1.2-2.4H28c0-0.3,0-0.7,0-1c1-11.2,10.5-20,21.9-20c13,0,23.3,11.3,21.9,24.5
                                C70.8,62,61.8,71,52.2,71.9c-7.1,0.7-13.8-1.9-18.5-7c-0.6-0.7-1.4-1.1-2.2-0.1l-2.4,2.9c-0.5,0.6-0.1,1,0.4,1.5
                                c5.4,5.7,12.8,8.9,20.8,8.8c14.4-0.2,26.5-11.6,27.5-26C79.1,35.7,66.1,22,50,22z"/>
                    </g>
                  </g>
                </svg>
              </i>} label="Recents" />
              <Tab icon={<i className="icon">
                <svg width="30px" height="30px" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g id="SVGRepo_bgCarrier" stroke-width="0" />
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                  <g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M14.5 11.5C14.5 12.6046 13.6046 13.5 12.5 13.5C11.3954 13.5 10.5 12.6046 10.5 11.5C10.5 10.3954 11.3954 9.5 12.5 9.5C13.6046 9.5 14.5 10.3954 14.5 11.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /> <path d="M14.5 5.25C14.0858 5.25 13.75 5.58579 13.75 6C13.75 6.41421 14.0858 6.75 14.5 6.75V5.25ZM10.5 6.75C10.9142 6.75 11.25 6.41421 11.25 6C11.25 5.58579 10.9142 5.25 10.5 5.25V6.75ZM15.25 6C15.25 5.58579 14.9142 5.25 14.5 5.25C14.0858 5.25 13.75 5.58579 13.75 6H15.25ZM13.75 7C13.75 7.41421 14.0858 7.75 14.5 7.75C14.9142 7.75 15.25 7.41421 15.25 7H13.75ZM13.75 6C13.75 6.41421 14.0858 6.75 14.5 6.75C14.9142 6.75 15.25 6.41421 15.25 6H13.75ZM15.25 4C15.25 3.58579 14.9142 3.25 14.5 3.25C14.0858 3.25 13.75 3.58579 13.75 4H15.25ZM14.5 6.75C14.9142 6.75 15.25 6.41421 15.25 6C15.25 5.58579 14.9142 5.25 14.5 5.25V6.75ZM10.5 5.25C10.0858 5.25 9.75 5.58579 9.75 6C9.75 6.41421 10.0858 6.75 10.5 6.75V5.25ZM11.25 6C11.25 5.58579 10.9142 5.25 10.5 5.25C10.0858 5.25 9.75 5.58579 9.75 6H11.25ZM9.75 7C9.75 7.41421 10.0858 7.75 10.5 7.75C10.9142 7.75 11.25 7.41421 11.25 7H9.75ZM9.75 6C9.75 6.41421 10.0858 6.75 10.5 6.75C10.9142 6.75 11.25 6.41421 11.25 6H9.75ZM11.25 4C11.25 3.58579 10.9142 3.25 10.5 3.25C10.0858 3.25 9.75 3.58579 9.75 4H11.25ZM6.05108 17.8992C5.71926 18.1471 5.65126 18.6171 5.89919 18.9489C6.14713 19.2807 6.61711 19.3487 6.94892 19.1008L6.05108 17.8992ZM18.0511 19.1008C18.3829 19.3487 18.8529 19.2807 19.1008 18.9489C19.3487 18.6171 19.2807 18.1471 18.9489 17.8992L18.0511 19.1008ZM14.5 6.75H15.5V5.25H14.5V6.75ZM15.5 6.75C17.2949 6.75 18.75 8.20507 18.75 10H20.25C20.25 7.37665 18.1234 5.25 15.5 5.25V6.75ZM18.75 10V16H20.25V10H18.75ZM18.75 16C18.75 17.7949 17.2949 19.25 15.5 19.25V20.75C18.1234 20.75 20.25 18.6234 20.25 16H18.75ZM15.5 19.25H9.5V20.75H15.5V19.25ZM9.5 19.25C7.70507 19.25 6.25 17.7949 6.25 16H4.75C4.75 18.6234 6.87665 20.75 9.5 20.75V19.25ZM6.25 16V10H4.75V16H6.25ZM6.25 10C6.25 8.20507 7.70507 6.75 9.5 6.75V5.25C6.87665 5.25 4.75 7.37665 4.75 10H6.25ZM9.5 6.75H10.5V5.25H9.5V6.75ZM13.75 6V7H15.25V6H13.75ZM15.25 6V4H13.75V6H15.25ZM14.5 5.25H10.5V6.75H14.5V5.25ZM9.75 6V7H11.25V6H9.75ZM11.25 6V4H9.75V6H11.25ZM6.94892 19.1008C10.2409 16.641 14.7591 16.641 18.0511 19.1008L18.9489 17.8992C15.1245 15.0416 9.87551 15.0416 6.05108 17.8992L6.94892 19.1008Z" fill="currentColor" /> </g>
                </svg>
              </i>} label="Contacts" />
              <Tab icon={<i className="icon">
                <svg width="30px" height="30px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="currentColor" stroke="currentColor">
                  <g id="SVGRepo_bgCarrier" stroke-width="0" />
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                  <g id="SVGRepo_iconCarrier"> <g id="icomoon-ignore"> </g> <path d="M6.937 21.865c-1.766 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM6.937 27.195c-1.176 0-2.132-0.956-2.132-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M6.937 3.738c-1.766 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM6.937 9.069c-1.176 0-2.132-0.956-2.132-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M6.937 12.779c-1.766 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM6.937 18.11c-1.176 0-2.132-0.957-2.132-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M16 21.865c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM16 27.195c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M16 3.738c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM16 9.069c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M16 12.779c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM16 18.11c-1.176 0-2.133-0.957-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M25.063 21.865c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM25.063 27.195c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> <path d="M25.063 10.135c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199zM25.063 4.805c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133c-1.176 0-2.133-0.956-2.133-2.133s0.956-2.132 2.133-2.132z" fill="currentColor"> </path> <path d="M25.063 12.779c-1.767 0-3.199 1.432-3.199 3.198s1.432 3.199 3.199 3.199c1.766 0 3.199-1.432 3.199-3.199s-1.433-3.198-3.199-3.198zM25.063 18.11c-1.176 0-2.133-0.957-2.133-2.133s0.956-2.132 2.133-2.132c1.176 0 2.133 0.956 2.133 2.132s-0.956 2.133-2.133 2.133z" fill="currentColor"> </path> </g>
                </svg>
              </i>} label="Keypad" />
              <Tab icon={<i className="icon">
                <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                  <g id="SVGRepo_bgCarrier" stroke-width="0" />
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                  <g id="SVGRepo_iconCarrier"> <path d="M6 16C8.20914 16 10 14.2091 10 12C10 9.79086 8.20914 8 6 8C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16ZM6 16H18M18 16C20.2091 16 22 14.2091 22 12C22 9.79086 20.2091 8 18 8C15.7909 8 14 9.79086 14 12C14 14.2091 15.7909 16 18 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /> </g>
                </svg>
              </i>} label="Voicemail" />
              <Tab icon={<i className="icon">
                <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                  <g id="SVGRepo_iconCarrier">
                    <path d="M8 2V5" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M16 2V5" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M3.5 9.09H20.5" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M11.9955 13.7H12.0045" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.29431 13.7H8.30329" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.29431 16.7H8.30329" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </g>
                </svg>
              </i>} label="AI Calling" />
              {/* <Tab icon={<i className="icon">
                <svg width="30px" height="30px" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                  <g id="SVGRepo_bgCarrier" stroke-width="0" />
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                  <g id="SVGRepo_iconCarrier"> <g id="Layer_2" data-name="Layer 2"> <g id="invisible_box" data-name="invisible box"> <rect width="48" height="48" fill="none" /> </g> <g id="icons_Q2" data-name="icons Q2"> <path d="M16,36a2,2,0,0,1-2-2V6a2,2,0,0,1,2-2h0a2,2,0,0,1,2,2V34a2,2,0,0,1-2,2Z" /> <path d="M24,36a2,2,0,0,1-2-2V6a2,2,0,0,1,2-2h0a2,2,0,0,1,2,2V34a2,2,0,0,1-2,2Z" /> <path d="M32,36a2,2,0,0,1-2-2V6a2,2,0,0,1,2-2h0a2,2,0,0,1,2,2V34a2,2,0,0,1-2,2Z" /> <path d="M39.7,26A2.1,2.1,0,0,0,38,28.1V40H10V28.1A2.1,2.1,0,0,0,8.3,26,2,2,0,0,0,6,28V42a2,2,0,0,0,2,2H40a2,2,0,0,0,2-2V28A2,2,0,0,0,39.7,26Z" /> <path d="M9.8,15.7,2.5,11.1c-.3-.2-.5-.1-.5.3v9.2c0,.4.2.5.5.3l7.3-4.6Q10.3,16,9.8,15.7Z" /> <path d="M45.8,15.7l-7.3-4.6c-.3-.2-.5-.1-.5.3v9.2c0,.4.2.5.5.3l7.3-4.6Q46.3,16,45.8,15.7Z" /> </g> </g> </g>
                </svg>
              </i>} label="Queue" /> */}
            </Tabs>
          </AppBar>
        </div>
      </div>
      </>
      )}

      {/* Time Warning Modal */}
      {showTimeWarning && pendingCallNumber && (
        <div className="time-warning-overlay">
          <div className="time-warning-modal">
            <div className="time-warning-header">
              <svg className="time-warning-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#666" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className="time-warning-message">
                It's around {getLocalTimeForPhoneNumber(pendingCallNumber)?.time} for them. Are you sure you want to call them?
              </p>
            </div>
            <div className="time-warning-content">
              {(() => {
                const normalizedNumber = pendingCallNumber.replace(/[^\d]/g, '');
                const contact = contacts.find(c => (c.phone || '').replace(/[^\d]/g, '') === normalizedNumber);
                return (
                  <div className="time-warning-contact-info">
                    <div className="time-warning-contact-avatar">
                      {contact ? getContactInitials(contact) : 'U'}
                    </div>
                    <div>
                      <div className="time-warning-contact-name">{contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : 'Unknown contact'}</div>
                      <div className="time-warning-contact-number">{pendingCallNumber}</div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="time-warning-buttons">
              <button className='btn-style-border time-warning-cancel-btn' onClick={() => { setShowTimeWarning(false); setPendingCallNumber(null); pendingCallContextRef.current = null; }}>
                Cancel
              </button>
              <button className='btn-style-new time-warning-call-btn' onClick={() => { setShowTimeWarning(false); makeCall(pendingCallNumber, true, pendingCallContextRef.current); setPendingCallNumber(null); }}>
                Call Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DialPad;
